import { ModelInputs } from "../../projects/domain/types";
import type { VersionStatus } from "../../model-version/types";
import type { ScenariosBlock } from "../../impact/domain/scenarios";

// ──────────────────────────────────────────────────────────────────────
// Spatial impact types (Phase 3: multi-scale map UI)
// ──────────────────────────────────────────────────────────────────────

export interface SpatialBreakdown {
  DALYs: number;
  PDFs: number;
  USD: number;
}

export type UpstreamSource =
  | "eia923_msha"
  | "eia923_gas_state"
  | "eia923_unresolved_msha_national_fallback"
  | "eia923_unknown_state_national_fallback"
  | "national_average";

export interface UpstreamAttributionEntry {
  basin_name: string;
  latitude: number;
  longitude: number;
  share: number;
  dalys_share: number;
  pdfs_share: number;
  usd_share: number;
  // Some older runs may not carry `source` — treat as national_average
  source?: UpstreamSource;
  mine_msha_id?: string | null;
  basin?: string | null;
  state?: string | null;
  county?: string | null;
}

export interface DisplacedGeneratorEnriched {
  plant_id: string | number;
  plant_name: string;
  fuel_type: string;
  state: string;
  county: string;
  latitude: number;
  longitude: number;
  capacity_mw: number;
  displaced_gwh: number;
  dalys?: number;
  pdfs?: number;
  usd?: number;
  emissions_by_category?: Record<string, number>;
  impacts_by_category?: Record<string, number>;
  spatial_breakdown?: Record<"global" | "regional" | "local", SpatialBreakdown>;
  upstream_attribution?: UpstreamAttributionEntry[] | null;
}

export interface AirshedFeatureProperties {
  plant_id: number | string;
  plant_name?: string;
  state?: string;
  intake_fraction?: number;
  meteorology_year?: number;
  [k: string]: any;
}

export interface AirshedFeatureCollection {
  type: "FeatureCollection";
  metadata?: {
    n_plants_requested?: number;
    n_plants_with_polygon?: number;
    source?: string;
    [k: string]: any;
  };
  features: Array<{
    type: "Feature";
    geometry: {
      type: "Polygon" | "MultiPolygon";
      coordinates: any;
    };
    properties: AirshedFeatureProperties;
  }>;
}

/**
 * Phase 4b receptor-sampled PM2.5 intake tile pyramid metadata.
 * The backend's /airshed-intensity-tiles endpoint returns this; the
 * frontend feeds `tile_url_template` to L.TileLayer.
 *
 * 404 from the backend → fall back to the polygon layer.
 */
export interface IntensityTileResponse {
  tile_url_template: string;
  min_zoom: number;
  max_zoom: number;
  tile_size: number;
  bounds_wgs84?: [number, number, number, number] | null;
  color_scale: {
    min: number;
    max: number;
    unit: string;
    ramp: string;
  };
  generator_count?: number | null;
  tile_count?: number | null;
  generated_at_iso?: string | null;
  citation?: string | null;
  /** Portfolio-only: number of projects summed into this aggregate raster. */
  n_projects?: number | null;
  /** Portfolio-only: project ids contributing to the aggregate. */
  contributing_projects?: string[] | null;
}

// NB: `VersionStatus` fields (result_model_version, current_model_version,
// is_outdated, changelog) are optional — the projects vertical of the backend
// attaches them to the latest run as it lands; until then they're undefined and
// the version UI degrades to a no-op.
export interface ModelResults extends VersionStatus {
    id: string;
    project_id: string;
    status: string;
    customer_id: string;
    mw_capacity: number;
    run_date: string;
    created_at: string | null;
    updated_at: string;
    year: number;         // Add this property
    run_type: string;
    start_date: string;
    end_date: string;
    profile_label?: string;
    /**
     * Optional Central ⇄ Range sensitivity bracket (Floor/Central/Ceiling).
     * Absent or status!=='complete' → dashboards render central only.
     */
    scenarios?: ScenariosBlock;
    summary_json: {
        Impacts?: {
            Operational_Analysis?: {
                Total?: {
                    USD: number;
                    PDFs: number;
                    DALYs: number;
                };
                Public_Health?: Array<{
                    Impact_Category: string;
                    Total_Impact: number;
                    USD: number;
                }>;
                Biodiversity?: Array<{
                    Impact_Category: string;
                    Total_Impact: number;
                    USD: number;
                }>;
                Economic?: Array<{
                    Impact_Category: string;
                    USD: number;
                }>;
            };
            Cradle_to_Grave_Analysis?: {
                // Same structure as Operational_Analysis
                Total?: {
                    USD: number;
                    PDFs: number;
                    DALYs: number;
                };
                Public_Health?: Array<any>;
                Biodiversity?: Array<any>;
                Economic?: Array<any>;
            };
        };
        Emissions?: {
            Operational_Analysis?: Record<string, any>;
            Cradle_to_Grave_Analysis?: Record<string, any>;
        };
        Generation?: Record<string, { Difference: number }>;
        // Benefit frame for customer-friendly avoided values
        benefit_frame?: {
            renewable_contribution?: {
                by_fuel_gwh?: Record<string, number>;
                total_gwh?: number;
                interpretation?: string;
            };
            fossil_displacement?: {
                by_fuel_gwh?: Record<string, number>;
                total_gwh?: number;
                interpretation?: string;
            };
            emissions_avoided?: Record<string, number>;
            impacts_avoided?: {
                dalys?: number;
                pdfs?: number;
                economic_usd?: number;
                total_usd?: number;
            };
            metadata?: Record<string, any>;
        };
        // Generation metadata
        generation_metadata?: {
            actual_generation_gwh?: number;
            capacity_factor?: number;
            profile_source?: string;
            tech_type?: string;
        };
        // Interconnection displacement breakdown
        interconnection_breakdown?: {
            total_unallocated_gwh: number;
            total_displaced_via_interconnector_gwh: number;
            neighbors: Array<{
                neighbor_area_code: string;
                neighbor_name: string;
                eco_geography: string;
                net_flow_gwh: number;
                displaced_generation: Record<string, number>;
                endpoint_dalys: number;
                endpoint_pdfs: number;
                endpoint_usd: number;
            }>;
            total_endpoint_dalys: number;
            total_endpoint_pdfs: number;
            total_endpoint_usd: number;
            methodology: string;
            notes: string[];
        } | null;
        // v1.3: Generator-level displacement attribution
        // Phase 3 enrichment: each generator may carry spatial_breakdown,
        // impacts_by_category, emissions_by_category, and upstream_attribution.
        generator_attribution?: {
            total_displaced_mwh: number;
            n_generators: number;
            n_counties: number;
            generators: DisplacedGeneratorEnriched[];
            counties_affected: Record<string, number>;
            states_affected: Record<string, number>;
        } | null;
        // v1.3: Congestion adjustment metadata
        congestion_metadata?: {
            avg_factor: number;
            constrained_hours: number;
            total_hours: number;
        } | null;
    };
}

export type Section = {
    title: string;
    keys: readonly (keyof ModelInputs)[];
    note?: string;
};

// Common impact data structure used across components
export interface ImpactData {
  projectId: string;
  dalys: number;  // Health impact (disability-adjusted life years)
  pdfs: number;   // Biodiversity impact (potentially disappeared fraction)
  economic: number; // Economic impact in USD
  mwCapacity: number; // Project capacity
  generationMwh: number; // Annual generation in MWh
}


// For display in components
export interface ImpactMetric {
  title: string;
  value: number;
  unit: string;
  icon: JSX.Element;
  color: string;
}

// For detailed analysis displays
export interface ImpactEntry {
  Impact_Category: string;
  Total_Impact?: number;
  USD: number;
  Percentage?: number;
}

// Function to extract impact data from model results
export function extractImpactFromModelRun(run: ModelResults): ImpactData | null {
  try {
    // Backend label swap: Cradle_to_Grave_Analysis = cradle-to-grave totals
    if (!run.summary_json?.Impacts?.Cradle_to_Grave_Analysis?.Total) return null;

    const netAnalysis = run.summary_json.Impacts.Cradle_to_Grave_Analysis.Total;
    
    // Extract generation from generation_metadata
    const genGwh = run.summary_json.generation_metadata?.actual_generation_gwh;
    const generationMwh = genGwh ? genGwh * 1000 : 0;

    return {
      projectId: run.project_id,
      dalys: typeof netAnalysis.DALYs === 'string' ? parseFloat(netAnalysis.DALYs) : netAnalysis.DALYs || 0,
      pdfs: typeof netAnalysis.PDFs === 'string' ? parseFloat(netAnalysis.PDFs) : netAnalysis.PDFs || 0,
      economic: typeof netAnalysis.USD === 'string' ? parseFloat(netAnalysis.USD) : netAnalysis.USD || 0,
      mwCapacity: typeof run.mw_capacity === 'string' ? parseFloat(run.mw_capacity) : run.mw_capacity || 0,
      generationMwh,
    };
  } catch (err) {
    console.error('Error extracting impact data:', err);
    return null;
  }
}

// Impact colors used across the application
export const impactColors = {
  health: '#FBBF24',   // Yellow/amber for health (DALYs)
  species: '#3B82F6',  // Blue for biodiversity (PDFs)
  economic: '#22C55E', // Green for economic (USD)
  capacity: '#8E44AD', // Purple for capacity
};